/**
 * SCHEDULE, at `/schedule`. Built from `faithfully-kept-business/design/home/Schedule.dc.html`, its
 * structure and its words, with ops's copy of the visits in place of the sample ones, as GET
 * /api/schedule answers them. The words are in `words.ts`.
 *
 * - THE ARTBOARD'S HEADING, "Every two weeks, on Tuesdays", under the eyebrow: each home's standing
 *   schedule, its cadence and weekday as ops sends them with the home (the seam, l.224 at b727ec1),
 *   in `words.ts`. With one home its line is the page's heading, as the artboard draws it. With
 *   several, which no artboard draws, the eyebrow is the heading and each line is under its home's
 *   name, as Home names a home among several, so a line is never read as every home's. Nothing is
 *   drawn for a home with no standing schedule, a one-time visit, or a word this build does not
 *   know, and the heading is drawn whatever the visits below it say, since it is the standing
 *   schedule, not a visit.
 * - A HOME'S PAUSE AND HER ASKS ABOUT ITS REGULAR CLEANS, under its line (round six, slice S3,
 *   `docs/seam.md` P84), which no artboard draws: a pause while it has not ended, "Paused from 1
 *   November until 15 March.", as Account and Your agreement say it (`pauseSaid`), or "Paused."
 *   where ops holds it paused with no days left to say (P1), and for a client
 *   where each of the household's asks about the home's regular cleans stands, in Account's own
 *   callouts (`CleansAskLines`), with nothing to press, since Account is where she asks. Nothing
 *   reads paused, resumed or changed before ops's push says so (CLAUDE.md binding rule 1): the line
 *   and the pause are as ops last pushed them, and an ask is said as asked: an ending she asked as a
 *   cancel as a cancel, as the ask itself says (`cancel`, the fifth client-journey walk's leftovers
 *   and their review, 2026-10-20), whatever ops has done with the agreement since. Raised by the
 *   replay of the owner's household on 2026-10-18, where Schedule said a home Account said was
 *   paused was cleaned every four weeks, and nothing of the asks she had just made.
 * - THE ARTBOARD'S CARDS, one for each visit from today on: its day, the cleaner and the window,
 *   each line added from the card with its price, where a skip or a move stands, and "Skip or move"
 *   wherever the Worker offers either. The artboard draws that button on the next visit alone; here
 *   every visit the Worker offers a skip or a move on has it, since the Worker decides what may be
 *   asked, and that difference is on the owner's list.
 * - ADD TO THIS VISIT (provisional P112), which no artboard draws: under "Skip or move" on a card
 *   from today on, wherever the Worker offers `visit.addon` and the card holds an add-on line home
 *   can quote (`card`), a button that opens the add-on sheet (`components/AddonSheet.tsx`). The ask
 *   is said in a callout of its own after a skip or a move's, as they are: sent, then waiting in the
 *   card's words, then the office's own words; and an add-on is drawn on the visit only once ops
 *   pushes it agreed, in the card's words with how many (`added`).
 * - A CLEAN DONE TODAY says "Your home is ready" under its day, as Home and Messages say it, with
 *   no window and no time, and the Worker offers no skip or move on it, which ops takes only on a
 *   planned or confirmed visit. From the next day it is no longer from today on, so it leaves
 *   Schedule; past cleans wait for "Every visit so far", which no artboard draws yet.
 * - WITH SEVERAL HOMES, which no artboard draws, each card names its home under its day, by its
 *   label alone (`homeNameOf`, provisional P40), in body ink as Payments and an invoice's page name
 *   a home, so two cleans on one day read apart; its "Skip or move" is described by that name, and
 *   the sheet it opens names the home too. With one home no card is named, as drawn. Each home's
 *   standing schedule in the heading is named in the same ink.
 * - A REQUEST IS SHOWN AS OPS HAS IT, AND NEVER AS MORE (CLAUDE.md binding rule 1). The newest
 *   skip or move about a visit is shown: sent to the office until ops says it has it, then waiting
 *   for us to confirm, then the office's own words once ops has decided, and nothing of home's own
 *   beside them. A visit shows as cancelled, its day crossed through and the words under it saying
 *   so, or on another day only once a push has made it so; nothing on this page changes a visit.
 * - SENT HERE. Once the door has written a request, the sheet closes, the card says sent to the
 *   office and takes the focus so it is read out, and the place is read again. Until that read
 *   carries the request, the card goes on saying sent and offers no second skip or move, even if
 *   the read did not go through. The same holds when the door answers after the sheet was closed,
 *   since what it takes to ask is held here for each visit, never by the sheet (`lib/asks.ts`).
 * - A SHEET WHOSE VISIT HAS GONE from a read since it opened, the visit gone or its home no longer
 *   hers, as the door's 404 finds it, stays drawn on the visit as it was, offering no way to ask, so
 *   what it says of that 404 is never taken away in silence (the owner, 2026-10-20); once she
 *   closes it, the focus goes to the page's heading, since the card that held its button has gone.
 * - A VISIT OPS SAYS CANNOT CHANGE: a clean under way shows no time, a visit the cleaner could not
 *   get into says the office will be in touch, and the Worker offers no skip or move on either.
 * - NOTHING YET: before any visit has come down it is the heading and the one line saying the rest
 *   is on its way, in the shell, so the nav still leads back to Home; with nothing from today on,
 *   it says so, and what next by the homes' regular cleans (`nothingAhead`, round six, slice S3):
 *   the next visit shows once the office has set it, where a home has regular cleans that are not
 *   paused today; that they are paused, where every home with any has them paused today; and to
 *   call to book only where no home has regular cleans, so "Call us to book a visit." never stands
 *   under a standing schedule. The holding screen, which has no nav, is Home's alone.
 * - LIST OR MONTH, which no artboard draws, asked for on the owner's walk of 2026-10-16: once any
 *   visit has come, a switch right under the heading, two buttons that say which is pressed, each
 *   44 tall. List is the artboard's cards, as above, and the default; Month is the visits on a
 *   month's days (`components/Month.tsx`), today's month first, with the visits before today the
 *   Worker answers in `earlier` as well. Her choice is kept on this device (`localStorage`), and a
 *   device that keeps nothing, or will not say, is given List and switches all the same.
 * - A CHOSEN DAY ON THE MONTH draws each of its visits with this file's own card, as the List draws
 *   it, "Skip or move" and its sheet included, so a request is shown there as here, but for its
 *   day, which the month names once over the day's visits, so several cleans on one day read as
 *   one day and not as headings alike (the final browser check of 2026-10-17). There, a card that
 *   names its home is headed by the name, so the cards of a shared day each have a title (the
 *   replay of the owner's walk, 2026-10-17); the List's dated card keeps its day as its heading, as
 *   drawn. A day before today offers no skip or move, as the List, which starts at today, never
 *   does; its cards speak in the past, and a clean ops still has under way on it is "this clean",
 *   never "today's" (`visitLine`); and a skip or a move on it is said only once ops has decided or
 *   closed it, never as sent, waiting or a line asking her to call (`requestSaid`).
 * - ONE CLEAN, WHERE A MESSAGE LED HER (`/schedule?visit=`, `scheduleVisitPath`; slice M1 of the
 *   phase 1 plan, `docs/seam.md` P104): "See this clean" under a copy of the office's text leads
 *   here with the clean's id, which the Worker gives only for a clean this place draws. A clean
 *   from today on is shown in the view she keeps: its card on the List, or the month on its day,
 *   chosen; a clean before today, which the List never holds, opens the month on its day for this
 *   visit alone, and her kept choice stays as it was. Its card is brought to the middle of the
 *   screen and takes the focus, so it is read out, once, when the page is first drawn: a read of
 *   the place again leaves her where she is. An id the place does not hold opens Schedule as ever.
 */
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getSchedule,
  type CardLine,
  type CleansAskShown,
  type RequestShown,
  type ScheduleHome,
  type SchedulePlace,
  type ScheduledVisit,
} from '../api.ts';
import { AddonSheet } from '../components/AddonSheet.tsx';
import { CARD_BUTTON } from '../components/buttons.ts';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { Month } from '../components/Month.tsx';
import { Check, MoveSheet } from '../components/MoveSheet.tsx';
import { Phone } from '../components/Phone.tsx';
import { focusPageHeading } from '../components/RouteFocus.tsx';
import { Shell } from '../components/Shell.tsx';
import { CleansAskLines } from '../components/CleansAsks.tsx';
import { askingFor, type Asking } from '../lib/asks.ts';
import { pauseSaid } from '../lib/cleans.ts';
import { dayInWords } from '../lib/dates.ts';
import { dayGone, homeNameOf, requestSaid, visitLine } from '../lib/visits.ts';
import { VISIT_QUERY } from '../paths.ts';
import {
  ADD_TO_VISIT,
  added,
  addonAsked,
  cadenceLine,
  FAILED_BEFORE_PHONE,
  NOTHING_SCHEDULED,
  NOTHING_SET_YET,
  NOTHING_WHILE_PAUSED,
  REQUEST,
  REST_ON_ITS_WAY,
  SCHEDULE,
  SCHEDULE_VIEW,
  theyAskedForAddon,
  theyAskedToMove,
  youAskedForAddon,
  youAskedToMoveTo,
} from '../words.ts';

const EYEBROW = 'font-mono text-small tracking-widest text-text-emphasis uppercase';

/** The two ways Schedule shows the visits. */
type View = 'list' | 'month';

/**
 * What a card asks about its visit, each from a sheet of its own: its day, a skip or a move, from
 * the skip or move sheet; or an add-on, from the add-on sheet (P112).
 */
type Asked = 'day' | 'addon';

/**
 * The visit a sheet is open on, and which sheet; and the visit as it was drawn when the sheet
 * opened, which keeps the sheet drawn where a read since no longer holds it.
 */
interface SheetOn {
  readonly visit: string;
  readonly sheet: Asked;
  readonly seen: ScheduledVisit;
}

/** Where this device keeps the view she chose. */
const VIEW_KEY = 'schedule.view';

/**
 * The view this device kept, or List. A browser may refuse the storage outright, or refuse to hand
 * it over at all, and either is List.
 */
function keptView(): View {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'month' ? 'month' : 'list';
  } catch {
    return 'list';
  }
}

/** Keeps the view she chose on this device, where it can; the page works the same where not. */
function keepView(view: View): void {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch {
    // Nothing kept: the next visit opens on List.
  }
}

const SWITCH =
  'flex min-h-11 items-center justify-center gap-1.5 rounded-control border-2 px-3 text-small';
const SWITCH_ON = 'border-surface-brand bg-surface-brand-soft font-semibold text-text-body';
const SWITCH_OFF =
  'border-border-strong bg-surface-raised text-text-body hover:bg-surface-accent-soft';

/** List or Month: two buttons, the one shown pressed, with the move sheet's check on it. */
function ViewSwitch({
  view,
  onChoose,
}: {
  readonly view: View;
  readonly onChoose: (view: View) => void;
}) {
  const choices: readonly { readonly view: View; readonly words: string }[] = [
    { view: 'list', words: SCHEDULE_VIEW.list },
    { view: 'month', words: SCHEDULE_VIEW.month },
  ];
  return (
    <div role="group" aria-label={SCHEDULE_VIEW.showAs} className="grid grid-cols-2 gap-2">
      {choices.map((choice) => {
        const on = choice.view === view;
        return (
          <button
            key={choice.view}
            type="button"
            aria-pressed={on}
            onClick={() => onChoose(choice.view)}
            className={`${SWITCH} ${on ? SWITCH_ON : SWITCH_OFF}`}
          >
            {on ? <Check size={18} /> : null}
            <span>{choice.words}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * A home's pause while it has not ended, in words, as Account says it (`pauseSaid`): "Paused from 1
 * November until 15 March.", or with no end from ops "Paused from 1 November.", or "Paused." where
 * ops holds it paused with no days left to say (P1); null for none, or a first day that is not real.
 */
const pauseLine = (home: ScheduleHome): string | null => pauseSaid(home);

/** Whether a home has regular cleans: a standing schedule that comes again, whatever its words. */
const hasRegularCleans = (home: ScheduleHome): boolean =>
  home.cadence !== undefined && home.cadence !== 'one_time';

/**
 * What Schedule says with nothing on it from today on (round six, slice S3): that the next visit
 * shows once the office has set it, where a home has regular cleans not paused today; that they are
 * paused, where every home with regular cleans has them paused today; and to call to book only where
 * no home has regular cleans.
 */
function nothingAhead(homes: readonly ScheduleHome[], today: string): string {
  const regular = homes.filter(hasRegularCleans);
  if (regular.length === 0) return NOTHING_SCHEDULED;
  // A pause with days is paused today from its first day on; one ops holds with none left to say
  // is paused now (P1).
  const pausedToday = (home: ScheduleHome) =>
    home.pausedFrom !== undefined ? home.pausedFrom <= today : home.paused === true;
  return regular.every(pausedToday) ? NOTHING_WHILE_PAUSED : NOTHING_SET_YET;
}

/**
 * The clean a link led her to (`?visit=`), as the place first drawn holds it: its id, its day, and
 * whether it is before today, so only the month holds it. Null for no link, or a clean the place
 * does not hold, or one before today with no day to open the month on.
 */
interface LedTo {
  readonly id: string;
  readonly day: string | undefined;
  readonly before: boolean;
}

function ledTo(visitId: string | null, schedule: SchedulePlace): LedTo | null {
  if (visitId === null) return null;
  const ahead = schedule.visits.find((visit) => visit.id === visitId);
  if (ahead !== undefined) return { id: visitId, day: ahead.date, before: false };
  const earlier = schedule.earlier.find((visit) => visit.id === visitId);
  if (earlier?.date !== undefined) return { id: visitId, day: earlier.date, before: true };
  return null;
}

/** One home as the heading draws it: its name among several, its line, its pause and its asks. */
interface HomeDrawn {
  readonly id: string;
  readonly name: string | undefined;
  readonly line: string | null;
  readonly pause: string | null;
  readonly asks: readonly CleansAskShown[];
}

/** What is said under a home's line: its pause, then where each ask about it stands. */
function UnderTheLine({ home }: { readonly home: HomeDrawn }) {
  return (
    <>
      {home.pause === null ? null : (
        <p className="text-body text-pretty text-text-body">{home.pause}</p>
      )}
      {home.asks.length === 0 ? null : <CleansAskLines requests={home.asks} />}
    </>
  );
}

/**
 * The eyebrow, and under it each home's standing schedule in words: with one home, its line as the
 * page's heading, as the artboard draws it; with several, each line under its home's name. Under
 * each line, the home's pause while it has not ended, and where each of the household's asks about
 * its regular cleans stands (round six, slice S3); a home with no line and an ask is drawn by its
 * name and the ask alone.
 */
function Heading({ homes }: { readonly homes: readonly ScheduleHome[] }) {
  const several = homes.length > 1;
  const drawn = homes.flatMap((home): HomeDrawn[] => {
    const line = cadenceLine(home.cadence, home.weekday);
    const asks = home.requests ?? [];
    if (line === null && asks.length === 0) return [];
    return [
      {
        id: home.id,
        name: several ? home.label : undefined,
        line,
        pause: line === null ? null : pauseLine(home),
        asks,
      },
    ];
  });
  const [only] = drawn;
  if (only === undefined) return <h1 className={EYEBROW}>{SCHEDULE.eyebrow}</h1>;
  if (!several) {
    return (
      <div>
        {only.line === null ? (
          <h1 className={EYEBROW}>{SCHEDULE.eyebrow}</h1>
        ) : (
          <>
            <p className={EYEBROW}>{SCHEDULE.eyebrow}</p>
            <h1 className="mt-1 font-serif text-h2 font-semibold text-pretty text-text-body">
              {only.line}
            </h1>
          </>
        )}
        <UnderTheLine home={only} />
      </div>
    );
  }
  return (
    <div>
      <h1 className={EYEBROW}>{SCHEDULE.eyebrow}</h1>
      {drawn.map((each) => (
        <div key={each.id} className="mt-2">
          {each.name === undefined ? null : <p className="text-body text-text-body">{each.name}</p>}
          {each.line === null ? null : (
            <p className="font-serif text-h3 font-semibold text-pretty text-text-body">
              {each.line}
            </p>
          )}
          <UnderTheLine home={each} />
        </div>
      ))}
    </div>
  );
}

export function Schedule(signed: Signed) {
  return (
    <Guarded load={getSchedule} signed={signed} at="schedule" heading={SCHEDULE.eyebrow}>
      {(schedule, reload) => <ScheduleDrawn schedule={schedule} signed={signed} reload={reload} />}
    </Guarded>
  );
}

interface DrawnProps {
  readonly schedule: SchedulePlace;
  readonly signed: Signed;
  readonly reload: () => void;
}

function ScheduleDrawn({ schedule, signed, reload }: DrawnProps) {
  /** The visit a sheet is open on, and which: the skip or move sheet, or the add-on sheet. */
  const [open, setOpen] = useState<SheetOn | null>(null);
  /** The same, as it is now, for a door's answer that comes after the sheet it was asked from. */
  const openNow = useRef<SheetOn | null>(null);
  /** Each skip or move the door wrote from this page, by the visit it is about. */
  const [sentHere, setSentHere] = useState<ReadonlyMap<string, string>>(new Map());
  /** Each add-on the door wrote from this page, by the visit it is about (P112). */
  const [addedHere, setAddedHere] = useState<ReadonlyMap<string, string>>(new Map());
  /**
   * Where the focus goes once a sheet has gone: back to the button that opened it, or to what the
   * card says of what it sent.
   */
  const [focus, setFocus] = useState<{
    readonly visit: string;
    readonly to: 'button' | 'said';
    readonly about: Asked;
  }>();
  const openers = useRef(new Map<string, HTMLButtonElement>());
  /** Each card's "Add to this visit", so the focus can come back to it. */
  const addOpeners = useRef(new Map<string, HTMLButtonElement>());
  const cards = useRef(new Map<string, HTMLElement>());
  /** Each visit's asking, which outlives the sheet it was asked from (`lib/asks.ts`). */
  const asks = useRef(new Map<string, Asking>());
  /** Each visit's asking for an add-on, held apart from its skip or move. */
  const addAsks = useRef(new Map<string, Asking>());
  const [params] = useSearchParams();
  /** The clean a message's link led her to, as the place was first drawn (slice M1). */
  const [led] = useState(() => ledTo(params.get(VISIT_QUERY), schedule));
  /** List or Month, as this device kept it, or the month for a clean before today a link led to. */
  const [view, setView] = useState<View>(() => (led?.before ? 'month' : keptView()));
  const choose = (chosen: View) => {
    setView(chosen);
    keepView(chosen);
  };
  /** Whether the clean a link led to has been given the focus, which happens once. */
  const ledShown = useRef(false);

  const openOn = (on: SheetOn | null) => {
    openNow.current = on;
    setOpen(on);
  };

  // Back to the button that opened the sheet, or to its card when a visit read again while the sheet
  // was open offers no such ask any more, so the button has gone; or, where the read took the card
  // away too, the visit gone or its home no longer hers, to the page's heading
  // (`focusPageHeading`).
  useEffect(() => {
    if (focus?.to !== 'button') return;
    const buttons = focus.about === 'addon' ? addOpeners : openers;
    const back = buttons.current.get(focus.visit) ?? cards.current.get(focus.visit);
    if (back === undefined) focusPageHeading();
    else back.focus();
  }, [focus]);

  // The clean a link led her to: in the middle of the screen, clear of the nav, and the focus on it
  // so it is read out. Once, as the page is first drawn.
  useEffect(() => {
    if (led === null || ledShown.current) return;
    ledShown.current = true;
    const card = cards.current.get(led.id);
    card?.scrollIntoView?.({ block: 'center' });
    card?.focus({ preventScroll: true });
  }, [led]);

  if (schedule.arriving.visits && schedule.visits.length === 0) {
    return (
      <Shell at="schedule" payments={schedule.household.payments}>
        <Heading homes={schedule.homes} />
        <p className="text-body text-pretty">{REST_ON_ITS_WAY}</p>
      </Shell>
    );
  }

  /**
   * The visit the open sheet is on, as the place now holds it. Where a read since the sheet opened
   * no longer holds it, the visit gone or its home no longer hers, the sheet stays drawn on it as it
   * was, offering no way to ask, so what the sheet says of the door's 404 is never taken away in
   * silence (the owner, 2026-10-20); she closes it as ever.
   */
  const sheetOn =
    open === null
      ? undefined
      : (schedule.visits.find((visit) => visit.id === open.visit) ?? { ...open.seen, offers: [] });
  const close = () => {
    if (open !== null) setFocus({ visit: open.visit, to: 'button', about: open.sheet });
    openOn(null);
  };
  /** Every visit the place answers, before today and from it, for the month. */
  const everyVisit = [...schedule.earlier, ...schedule.visits];
  /** The switch is drawn once any visit has come. */
  const switchable = everyVisit.length > 0;

  /**
   * One visit's card, on the List with its day and under a chosen day on the month without it. A
   * visit before today offers no skip or move: the List, which starts at today, never offers one
   * either.
   */
  const drawCard = (visit: ScheduledVisit, dated: boolean): ReactNode => (
    <VisitCard
      visit={visit}
      dated={dated}
      home={homeNameOf(visit, schedule.homes)}
      today={schedule.today}
      askable={!dayGone(visit, schedule.today)}
      lines={schedule.card}
      sentHere={sentHere.get(visit.id)}
      addedHere={addedHere.get(visit.id)}
      focusSaid={focus?.visit === visit.id && focus.to === 'said' ? focus.about : null}
      onSkipOrMove={() => {
        setFocus(undefined);
        openOn({ visit: visit.id, sheet: 'day', seen: visit });
      }}
      onAdd={() => {
        setFocus(undefined);
        openOn({ visit: visit.id, sheet: 'addon', seen: visit });
      }}
      opener={(button) => {
        if (button) openers.current.set(visit.id, button);
        else openers.current.delete(visit.id);
      }}
      addOpener={(button) => {
        if (button) addOpeners.current.set(visit.id, button);
        else addOpeners.current.delete(visit.id);
      }}
      card={(article) => {
        if (article) cards.current.set(visit.id, article);
        else cards.current.delete(visit.id);
      }}
    />
  );

  // The door may answer after the sheet has closed. The card still says sent and takes the focus,
  // and the place is still read again, unless another sheet is open by then, on another visit or
  // for the other ask, which is left as it is.
  const sent = (visitId: string, requestId: string, about: Asked) => {
    const held = about === 'addon' ? setAddedHere : setSentHere;
    held((each) => new Map(each).set(visitId, requestId));
    const now = openNow.current;
    if (now === null || (now.visit === visitId && now.sheet === about)) {
      setFocus({ visit: visitId, to: 'said', about });
      openOn(null);
    }
    reload();
  };

  const sheet =
    sheetOn === undefined || open === null ? undefined : open.sheet === 'addon' ? (
      <AddonSheet
        visit={sheetOn}
        home={homeNameOf(sheetOn, schedule.homes)}
        today={schedule.today}
        household={schedule.household.id}
        card={schedule.card}
        asking={askingFor(addAsks.current, sheetOn.id)}
        onClose={close}
        onSent={(requestId) => sent(sheetOn.id, requestId, 'addon')}
        onSignedOut={signed.onSignedOut}
        onStale={reload}
      />
    ) : (
      <MoveSheet
        visit={sheetOn}
        home={homeNameOf(sheetOn, schedule.homes)}
        today={schedule.today}
        household={schedule.household.id}
        asking={askingFor(asks.current, sheetOn.id)}
        onClose={close}
        onSent={(requestId) => sent(sheetOn.id, requestId, 'day')}
        onSignedOut={signed.onSignedOut}
        onStale={reload}
      />
    );

  return (
    <Shell at="schedule" payments={schedule.household.payments} over={sheet}>
      <Heading homes={schedule.homes} />
      {switchable ? <ViewSwitch view={view} onChoose={choose} /> : null}
      {switchable && view === 'month' ? (
        <Month
          today={schedule.today}
          visits={everyVisit}
          card={(visit) => drawCard(visit, false)}
          opensOn={led?.day}
        />
      ) : schedule.visits.length === 0 ? (
        <p className="text-body text-pretty">{nothingAhead(schedule.homes, schedule.today)}</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {schedule.visits.map((visit) => (
            <li key={visit.id}>{drawCard(visit, true)}</li>
          ))}
        </ol>
      )}
    </Shell>
  );
}

/** What a card says about a request: a line, and whether the phone follows it. */
interface Said {
  readonly text: string;
  readonly phone?: boolean;
}

/** The request types that change a visit's day, which Schedule shows. */
const CHANGES_THE_DAY: ReadonlySet<string> = new Set(['visit.skip', 'visit.move']);

/** An add-on to a visit, which Schedule shows apart from a skip or a move (P112). */
const ADDS_TO_IT: ReadonlySet<string> = new Set(['visit.addon']);

/** A skip or a move ops has, and has not decided: whose it is, and what it asked. */
function waiting(request: RequestShown): string {
  if (request.type === 'visit.move') {
    if (!request.mine) {
      return request.askedBy === undefined ? REQUEST.waiting : theyAskedToMove(request.askedBy);
    }
    const [only, ...more] = request.daysThatSuit;
    const day = only !== undefined && more.length === 0 ? dayInWords(only) : null;
    return day === null ? REQUEST.youMove : youAskedToMoveTo(day);
  }
  return request.mine ? REQUEST.youSkip : REQUEST.waiting;
}

/**
 * An add-on ops has, and has not decided (P112): whose it is, and what it asked, in the card's
 * words and with how many, or with no words for what where the card no longer holds its line.
 */
function addonWaiting(request: RequestShown, lines: readonly CardLine[]): string {
  const line = lines.find((each) => each.code === request.rateLineCode);
  const what = line === undefined ? undefined : addonAsked(line.label, request.quantity);
  if (request.mine) return youAskedForAddon(what);
  return request.askedBy === undefined ? REQUEST.waiting : theyAskedForAddon(request.askedBy, what);
}

/**
 * Where the newest request of `types` about a visit stands, in words, or null when there is nothing
 * to say: ops's decision with no words of the office's, a reason no line is written for yet, or a
 * state this build does not know. A decision is only ever the office's own words. On a day before
 * today, only what ops has decided or closed is said (`requestSaid`, `lib/visits.ts`). A skip or a
 * move is said this way, and since P112 an add-on, each in a callout of its own.
 */
function said(
  visit: ScheduledVisit,
  types: ReadonlySet<string>,
  sentHere: string | undefined,
  today: string,
  waitingWords: (request: RequestShown) => string,
): Said | null {
  if (sentHere !== undefined && !visit.requests.some((request) => request.id === sentHere)) {
    return requestSaid(visit, 'sent', today) ? { text: REQUEST.sent } : null;
  }
  const request = visit.requests.find((each) => types.has(each.type));
  if (request === undefined || !requestSaid(visit, request.state, today)) return null;
  switch (request.state) {
    case 'sent':
      return { text: REQUEST.sent };
    case 'received':
      return { text: waitingWords(request) };
    case 'accepted':
    case 'declined':
      return request.answer === undefined ? null : { text: request.answer };
    case 'cancelled':
      if (request.reason === 'visit_no_longer_open') return { text: REQUEST.closed };
      if (request.reason === 'withdrawn') return { text: REQUEST.withdrawn };
      if (request.reason === 'membership_revoked' && !request.mine) {
        return { text: REQUEST.revoked };
      }
      return null;
    case 'refused':
      return {
        text: request.code === 'not_open' ? REQUEST.closedBeforePhone : FAILED_BEFORE_PHONE,
        phone: true,
      };
    case 'stopped':
      return { text: FAILED_BEFORE_PHONE, phone: true };
    default:
      return null;
  }
}

interface CardProps {
  readonly visit: ScheduledVisit;
  /**
   * Whether the card names its day: on the List, yes; on the month, whose panel names it, no, and
   * there a card that names its home is headed by the name instead.
   */
  readonly dated: boolean;
  /** The name of the visit's home, when she sees several homes and it has one. */
  readonly home: string | undefined;
  /** The business's day, as the Worker answers it: a clean under way is today's only on it. */
  readonly today: string;
  /** Whether "Skip or move" and "Add to this visit" may be offered at all: never on a day gone. */
  readonly askable: boolean;
  /** The card's add-on lines she may ask to add, which "Add to this visit" needs one of (P112). */
  readonly lines: readonly CardLine[];
  /** The id of a skip or move the door wrote from this page about this visit. */
  readonly sentHere: string | undefined;
  /** The id of an add-on the door wrote from this page about this visit (P112). */
  readonly addedHere: string | undefined;
  /** Which of what the card says takes the focus, having just been sent, or none. */
  readonly focusSaid: Asked | null;
  readonly onSkipOrMove: () => void;
  readonly onAdd: () => void;
  /** Where the card's "Skip or move" is, so the focus can come back to it. */
  readonly opener: (button: HTMLButtonElement | null) => void;
  /** Where the card's "Add to this visit" is, so the focus can come back to it. */
  readonly addOpener: (button: HTMLButtonElement | null) => void;
  /** Where the card is, for the focus to come back to when the button that opened a sheet has gone. */
  readonly card: (article: HTMLElement | null) => void;
}

/** Whether a request the door wrote from this page is not yet in what the place read. */
const notReadYet = (visit: ScheduledVisit, sent: string | undefined): boolean =>
  sent !== undefined && !visit.requests.some((each) => each.id === sent);

/** What a card says of a request, in its callout, and the phone after it where it asks her to call. */
function Callout({
  words,
  callout,
}: {
  readonly words: Said;
  readonly callout: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={callout}
      tabIndex={-1}
      className="mt-2 rounded-control border-2 border-border-brand bg-surface-accent-soft px-4 py-3"
    >
      <p className="text-small text-pretty text-text-body">
        {words.text}
        {words.phone ? (
          <>
            {' '}
            <Phone after="." />
          </>
        ) : null}
      </p>
    </div>
  );
}

function VisitCard({
  visit,
  dated,
  home,
  today,
  askable,
  lines: cardLines,
  sentHere,
  addedHere,
  focusSaid,
  onSkipOrMove,
  onAdd,
  opener,
  addOpener,
  card,
}: CardProps) {
  const callout = useRef<HTMLDivElement>(null);
  const addCallout = useRef<HTMLDivElement>(null);
  const nameId = useId();
  useEffect(() => {
    if (focusSaid === 'day') callout.current?.focus();
    if (focusSaid === 'addon') addCallout.current?.focus();
  }, [focusSaid]);

  const cancelled = visit.status === 'cancelled';
  const day = !dated || visit.date === undefined ? null : dayInWords(visit.date);
  const line = visitLine(visit, today);
  // An add-on ops agreed on the visit, in the card's words, with how many and the price for one as
  // ops pushed them (P112), never a sum of home's own.
  const lines = cancelled
    ? []
    : visit.addons.flatMap((addon) =>
        addon.label === undefined ? [] : [added(addon.label, addon.quantity, addon.unitPriceCents)],
      );
  const words = said(visit, CHANGES_THE_DAY, sentHere, today, waiting);
  const addWords = said(visit, ADDS_TO_IT, addedHere, today, (request) =>
    addonWaiting(request, cardLines),
  );
  // On the month, where the day is named over the cards, a card that names a home is headed by it.
  const titled = !dated && home !== undefined;
  const offered =
    askable &&
    !notReadYet(visit, sentHere) &&
    (visit.offers.includes('visit.skip') || visit.offers.includes('visit.move'));
  // "Add to this visit" where the Worker offers an add-on and the card holds a line home can quote,
  // and not while one sent from here is not yet in what the place read (P112).
  const addOffered =
    askable &&
    !notReadYet(visit, addedHere) &&
    visit.offers.includes('visit.addon') &&
    cardLines.length > 0;

  return (
    <article
      ref={card}
      tabIndex={-1}
      className="rounded-card border border-border-subtle bg-surface-raised px-5 py-4"
    >
      {day ? (
        <h2 className="text-body font-semibold text-text-body">{cancelled ? <s>{day}</s> : day}</h2>
      ) : null}
      {home === undefined ? null : titled ? (
        <h3 id={nameId} className="text-body font-semibold text-text-body">
          {home}
        </h3>
      ) : (
        <p id={nameId} className="text-body text-text-body">
          {home}
        </p>
      )}
      {line ? <p className="text-small text-balance text-text-body">{line}</p> : null}
      {lines.map((each, index) => (
        <p key={index} className="text-small text-text-muted">
          {each}
        </p>
      ))}
      {words ? <Callout words={words} callout={callout} /> : null}
      {addWords ? <Callout words={addWords} callout={addCallout} /> : null}
      {offered ? (
        <button
          ref={opener}
          type="button"
          aria-haspopup="dialog"
          aria-describedby={home === undefined ? undefined : nameId}
          onClick={onSkipOrMove}
          className={`mt-3 ${CARD_BUTTON}`}
        >
          {SCHEDULE.skipOrMove}
        </button>
      ) : null}
      {addOffered ? (
        <button
          ref={addOpener}
          type="button"
          aria-haspopup="dialog"
          aria-describedby={home === undefined ? undefined : nameId}
          onClick={onAdd}
          className={`${offered ? 'mt-2' : 'mt-3'} ${CARD_BUTTON}`}
        >
          {ADD_TO_VISIT.open}
        </button>
      ) : null}
    </article>
  );
}
